import * as API from '@/api/endpoints';
import * as Repository from '@/db/repositories';
import { DBMetadataKey, isDBEntryStale, setDBUpdatedNow } from '@/db/helpers/dbMetadata';

const genericSync = async <T>(
  apiFn: () => Promise<T[]>,
  // eslint-disable-next-line no-unused-vars
  dbRepo: { bulkAdd: (items: T[]) => Promise<T[]> },
  dbKey: DBMetadataKey,
  options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> },
) => {
  const { forceUpdate = false } = options ?? {};

  // we handle offline through the axios client controller

  const isStale = await isDBEntryStale(dbKey, options?.metadataDict);
  if (!isStale && !forceUpdate) return;

  try {
    const data = await apiFn();
    await dbRepo.bulkAdd(data);
    await setDBUpdatedNow(dbKey);
  } catch (error) {
    console.error(`Failed to sync ${dbKey} from API:`, error);
    // TODO: Save error to ErrorLogRepo
    throw error;
  }
};

// ---- Sync functions ----

export const syncHeadTypeLofts = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadTypeLofts, Repository.HeadTypeLoftDB, 'getHeadTypeLofts', options);
};

export const syncHeadTypeFamilies = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadTypeFamilies, Repository.HeadTypeFamilyDB, 'getHeadTypeFamilies', options);
};

export const syncFamilies = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getFamily, Repository.FamilyDB, 'getFamilies', options);
};

export const syncFedExCupPlayers = async (options?: { forceUpdate?: boolean; metaDataDict?: Record<string, string> }) => {
  return genericSync(API.getFedExCupPlayers, Repository.FedExCupPlayerDB, 'getFedExCupPlayers', options);
};

export const syncHeadModelHandsForDropdown = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadModelHandsForDropdown, Repository.HeadModelHandForDropdownDtoDB, 'getHeadModelHandsForDropdown', options);
};

export const syncHeadModelByManufacturerAndTypeForDropdownDto = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  // TODO: Fix Metal implementation currently only fetching 1/ Driver
  return genericSync(() => API.GetHeadModelsByManufacturerAndTypeForDropdown(41, 1), Repository.HeadModelForDropdownDtoDB, 'getHeadModelsByManufacturerAndTypeForDropdown', options);
};
// headType 1 = Driver | headType 4 = Iron
// TODO: Update backend API to support all models without head type filter
export const syncIronHeadModelByManufacturerAndTypeForDropdownDto = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.GetHeadModelsByManufacturerAndTypeForDropdown(41, 4), Repository.IronHeadModelForDropdownDtoDB, 'getIronHeadModelsByManufacturerAndTypeForDropdown', options);
};

export const syncHeadModelByManufacturerAndTypeForDropdownDtoWedges = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.GetHeadModelsByManufacturerAndTypeForDropdown(41, 5), Repository.HeadModelForDropdownDtoWedgesDB, 'getHeadModelsByManufacturerAndTypeForDropdownWedges', options);
};

export const syncHeadModelByManufacturerAndTypeForDropdownDtoFairways = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.GetHeadModelsByManufacturerAndTypeForDropdown(41, 2), Repository.HeadModelForDropdownDtoFairwayDB, 'getHeadModelsByManufacturerAndTypeForDropdownFairway', options);
};

export const syncHeadModelByManufacturerAndTypeForDropdownDtoHybrids = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.GetHeadModelsByManufacturerAndTypeForDropdown(41, 3), Repository.HeadModelForDropdownDtoHybridDB, 'getHeadModelsByManufacturerAndTypeForDropdownHybrid', options);
};

export const syncHandLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHandLookups, Repository.HandLookupDB, 'getHandLookup', options);
};

export const syncCtLocations = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getCtLocations, Repository.CtLocationDB, 'getCtLocations', options);
};

export const syncSleeves = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getSleeves, Repository.SleeveDB, 'getSleeves', options);
};

export const syncSwingWtPrefixes = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getSwingWtPrefix, Repository.SwingWtPrefixDB, 'getSwingWtPrefix', options);
};

export const syncSwingWtSuffixes = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getSwingWtSuffix, Repository.SwingWtSuffixDB, 'getSwingWtSuffix', options);
};

export const syncGripSizes = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getGripSizes, Repository.GripSizeDB, 'getGripSizes', options);
};

export const syncFractions = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getFraction, Repository.FractionDB, 'getFractions', options);
};

export const syncHeadWeightLocationLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightLocationLookups, Repository.HeadWeightLocationLookupDB, 'getHeadWeightLocationLookups', options);
};

export const syncHeadWeightLocations = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightLocations, Repository.HeadWeightLocationDB, 'getHeadWeightLocations', options);
};

export const syncHeadWeightNumbers = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightNumbers, Repository.HeadWeightNumberDB, 'getHeadWeightNumbers', options);
};

export const syncHeadWeightNumberLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightNumberLookups, Repository.HeadWeightNumberLookupDB, 'getHeadWeightNumberLookups', options);
};

export const syncHeadWeightLocationAft = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightLocationAft, Repository.HeadWeightLocationAftDB, 'getHeadWeightLocationAft', options);
};

export const syncHeadWeightNumberAft = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightNumberAft, Repository.HeadWeightNumberAftDB, 'getHeadWeightNumberAft', options);
};

export const syncHeadWeightLocationBack = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightLocationBack, Repository.HeadWeightLocationBackDB, 'getHeadWeightLocationBack', options);
};

export const syncHeadWeightNumberBack = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getHeadWeightNumberBack, Repository.HeadWeightNumberBackDB, 'getHeadWeightNumberBack', options);
};

export const syncShaftByShaftNameForDropdowns = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getShaftByShaftNameForDropdown, Repository.ShaftByShaftNameForDropdownDB, 'getShaftByShaftNameForDropdown', options);
};

export const syncIronShaftByShaftNameForDropdowns = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.getShaftByShaftNameForDropdown('Iron'), Repository.IronShaftByShaftNameForDropdownDB, 'getIronShaftByShaftNameForDropdown', options);
};

export const syncWedgeShaftByShaftNameForDropdowns = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.getShaftByShaftNameForDropdown('Wedge'), Repository.WedgeShaftByShaftNameForDropdownDB, 'getWedgeShaftByShaftNameForDropdown', options);
};

export const syncHybridShaftByShaftNameForDropdowns = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.getShaftByShaftNameForDropdown('Hybrid'), Repository.HybridShaftByShaftNameForDropdownDB, 'getHybridShaftByShaftNameForDropdown', options);
};

export const syncShaftLengthDigits = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getShaftLengthDigit, Repository.ShaftLengthDigitDB, 'getShaftLengthDigits', options);
};

export const syncShaftLengthFractions = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getShaftLengthFraction, Repository.ShaftLengthFractionDB, 'getShaftLengthFractions', options);
};

export const syncShaftTippingFractions = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getShaftTippingFraction, Repository.ShaftTippingFractionDB, 'getShaftTippingFractions', options);
};

export const syncSettings = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getSettings, Repository.SettingDB, 'getSettings', options);
};

export const syncGripTypes = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getGripTypes, Repository.GripTypeDB, 'getGripTypes', options);
};

export const syncDigits = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getDigit, Repository.DigitDB, 'getDigits', options);
};

export const syncLieDigits = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getLieDigits, Repository.LieDigitDB, 'getLieDigits', options);
};

export const syncLieFractions = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getLieFractions, Repository.LieFractionDB, 'getLieFractions', options);
};

export const syncLofts = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getLofts, Repository.LoftDB, 'getLofts', options);
};

export const syncLoftDigits = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getLoftDigits, Repository.LoftDigitDB, 'getLoftDigits', options);
};

export const syncLoftFractions = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getLoftFractions, Repository.LoftFractionDB, 'getLoftFractions', options);
};

export const syncPutterFace = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterFaces, Repository.PutterFaceDB, 'getPutterFaces', options);
};

export const syncPutterGrip = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterGrips, Repository.PutterGripDB, 'getPutterGrips', options);
};

export const syncPutterHeadFinish = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterHeadFinishes, Repository.PutterHeadFinishDB, 'getPutterHeadFinishes', options);
};

export const syncPutterHeadModel = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterHeadModels, Repository.PutterHeadModelDB, 'getPutterHeadModels', options);
};

export const syncPutterLevel = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterLevels, Repository.PutterLevelDB, 'getPutterLevels', options);
};

export const syncPutterNeck = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterNecks, Repository.PutterNeckDB, 'getPutterNecks', options);
};

export const syncPutterShaft = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterShafts, Repository.PutterShaftDB, 'getPutterShafts', options);
};

export const syncPutterSightline = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterSightlines, Repository.PutterSightlineDB, 'getPutterSightlines', options);
};

export const syncPutterType = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterTypes, Repository.PutterTypeDB, 'getPutterTypes', options);
};

export const syncPutterWeight = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPutterWeights, Repository.PutterWeightDB, 'getPutterWeights', options);
};

export const syncHeadFinishWedge = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(() => API.getHeadFinishes(5), Repository.HeadFinishWedgeDB, 'getHeadFinishes', options);
};

export const syncIronClub = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getIronClubs, Repository.IronClubDB, 'getIronClubs', options);
};

export const syncBsgBall = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgBalls, Repository.BsgBallDB, 'getBsgBalls', options);
};

export const syncBsgGlove = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgGloves, Repository.BsgGloveDB, 'getBsgGloves', options);
};

export const syncBsgGloveSize = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgGloveSizes, Repository.BsgGloveSizeDB, 'getBsgGloveSizes', options);
};

export const syncBsgHeadwear = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgHeadwear, Repository.BsgHeadwearDB, 'getBsgHeadwear', options);
};

export const syncBsgShoe = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgShoes, Repository.BsgShoeDB, 'getBsgShoes', options);
};

export const syncBsgShoeSize = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgShoeSizes, Repository.BsgShoeSizeDB, 'getBsgShoeSizes', options);
};

export const syncBsgShoeWidth = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBsgShoeWidth, Repository.BsgShoeWidthDB, 'getBsgShoeWidths', options);
};

export const syncGolfBag = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getGolfBags, Repository.GolfBagDB, 'getGolfBags', options);
};

export const syncTours = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getTours, Repository.TourDB, 'getTours', options);
};

export const syncLoftsAndHandAndHeadModel = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getLoftsAndHandAndHeadModel, Repository.LoftsAndHandAndHeadModelDB, 'getLoftsAndHandAndHeadModel', options);
};

export const syncPlayerRegionLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPlayerRegionLookups, Repository.PlayerRegionLookupsDB, 'getPlayerRegionLookups', options);
};

export const syncPlayerStatusLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPlayerStatusLookups, Repository.PlayerStatusLookupsDB, 'getPlayerStatusLookups', options);
};

export const syncPlayerInternalStatuses = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPlayerInternalStatuses, Repository.PlayerInternalStatusesDB, 'getPlayerInternalStatuses', options);
};

export const syncInternalStatusLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getInternalStatusLookups, Repository.InternalStatusLookupsDB, 'getInternalStatusLookups', options);
};

export const syncNationalities = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getNationalities, Repository.NationalityDB, 'getNationalities', options);
};

export const syncPlayerCollegeLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPlayerCollegeLookups, Repository.PlayerCollegeLookupDB, 'getPlayerCollegeLookups', options);
};

export const syncPlayerYearLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPlayerYearLookups, Repository.PlayerYearLookupDB, 'getPlayerYearLookups', options);
};

export const syncPlayerSupportLookups = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getPlayerSupportLookups, Repository.PlayerSupportLookupDB, 'getPlayerSupportLookups', options);
};

// ---- Data functions ----

export const getHeadTypeLoftData = async (headTypeId: number) => {
  await syncHeadTypeLofts();
  const lofts = await Repository.HeadTypeLoftDB.list();
  return lofts.filter((loft) => loft.headTypeId == headTypeId);
};

export const getHeadTypeFamilyData = async (headTypeId: number) => {
  await syncHeadTypeFamilies();
  const families = await Repository.HeadTypeFamilyDB.list();
  return families.filter((family) => family.headTypeId == headTypeId);
};

export const getFamilyData = async (ids?: number[]) => {
  await syncFamilies();
  const families = await Repository.FamilyDB.list();
  if (ids) {
    return families.filter((f) => ids.includes(f.familyId)).sort((a, b) => (a.sortId || 0) - (b.sortId || 0));
  }
  return families;
};

export const getFedExCupPlayerData = async () => {
  await syncFedExCupPlayers();
  return Repository.FedExCupPlayerDB.list();
};

export const getHeadModelHandForDropdownDtoData = async () => {
  await syncHeadModelHandsForDropdown();
  return Repository.HeadModelHandForDropdownDtoDB.list();
};

export const getHeadModelsByManufacturerAndTypeForDropdownData = async () => {
  await syncHeadModelByManufacturerAndTypeForDropdownDto();
  return Repository.HeadModelForDropdownDtoDB.list();
};

export const getHeadModelsByManufacturerAndTypeForDropdownDataWedges = async () => {
  await syncHeadModelByManufacturerAndTypeForDropdownDtoWedges();
  return Repository.HeadModelForDropdownDtoWedgesDB.list();
};

export const getHeadModelsByManufacturerAndTypeForDropdownDataFairways = async () => {
  await syncHeadModelByManufacturerAndTypeForDropdownDtoFairways();
  return Repository.HeadModelForDropdownDtoFairwayDB.list();
};

export const getHeadModelsByManufacturerAndTypeForDropdownDataHybrids = async () => {
  await syncHeadModelByManufacturerAndTypeForDropdownDtoHybrids();
  return Repository.HeadModelForDropdownDtoHybridDB.list();
};

// Duplicated since the current API requires one head type per request
export const getIronHeadModelsByManufacturerAndTypeForDropdownData = async () => {
  await syncIronHeadModelByManufacturerAndTypeForDropdownDto();
  return Repository.IronHeadModelForDropdownDtoDB.list();
};

export const getHandLookupData = async () => {
  await syncHandLookups();
  return Repository.HandLookupDB.list();
};

export const getCtLocationData = async () => {
  await syncCtLocations();
  return Repository.CtLocationDB.list();
};

export const getSleeveData = async () => {
  await syncSleeves();
  return Repository.SleeveDB.list();
};

export const getSwingWtPrefixData = async () => {
  await syncSwingWtPrefixes();
  return Repository.SwingWtPrefixDB.list();
};

export const getSwingWtSuffixData = async () => {
  await syncSwingWtSuffixes();
  return Repository.SwingWtSuffixDB.list();
};

export const getGripSizeData = async () => {
  await syncGripSizes();
  return Repository.GripSizeDB.list();
};

export const getFractionData = async () => {
  await syncFractions();
  return Repository.FractionDB.list();
};

export const getHeadWeightLocationLookupData = async () => {
  await syncHeadWeightLocationLookups();
  return Repository.HeadWeightLocationLookupDB.list();
};

export const getHeadWeightLocationData = async () => {
  await syncHeadWeightLocations();
  return Repository.HeadWeightLocationDB.list();
};

export const getHeadWeightNumberData = async () => {
  await syncHeadWeightNumbers();
  return Repository.HeadWeightNumberDB.list();
};

export const getHeadWeightNumberLookupData = async () => {
  await syncHeadWeightNumberLookups();
  return Repository.HeadWeightNumberLookupDB.list();
};

export const getHeadWeightLocationAftData = async () => {
  await syncHeadWeightLocationAft();
  return Repository.HeadWeightLocationAftDB.list();
};

export const getHeadWeightNumberAftData = async () => {
  await syncHeadWeightNumberAft();
  return Repository.HeadWeightNumberAftDB.list();
};

export const getHeadWeightLocationBackData = async () => {
  await syncHeadWeightLocationBack();
  return Repository.HeadWeightLocationBackDB.list();
};

export const getHeadWeightNumberBackData = async () => {
  await syncHeadWeightNumberBack();
  return Repository.HeadWeightNumberBackDB.list();
};

export const getShaftByShaftNameForDropdownData = async () => {
  await syncShaftByShaftNameForDropdowns();
  return Repository.ShaftByShaftNameForDropdownDB.list();
};

export const getIronShaftByShaftNameForDropdownData = async () => {
  await syncIronShaftByShaftNameForDropdowns();
  return Repository.IronShaftByShaftNameForDropdownDB.list();
};

export const getWedgeShaftByShaftNameForDropdownData = async () => {
  await syncWedgeShaftByShaftNameForDropdowns();
  return Repository.WedgeShaftByShaftNameForDropdownDB.list();
};

export const getHybridShaftByShaftNameForDropdownData = async () => {
  await syncHybridShaftByShaftNameForDropdowns();
  return Repository.HybridShaftByShaftNameForDropdownDB.list();
};

export const getShaftLengthDigitData = async () => {
  await syncShaftLengthDigits();
  return Repository.ShaftLengthDigitDB.list();
};

export const getShaftLengthFractionData = async () => {
  await syncShaftLengthFractions();
  return Repository.ShaftLengthFractionDB.list();
};

export const getShaftTippingFractionData = async () => {
  await syncShaftTippingFractions();
  return Repository.ShaftTippingFractionDB.list();
};

export const getSettingsData = async () => {
  await syncSettings();
  return Repository.SettingDB.list();
};

export const getGripTypeData = async () => {
  await syncGripTypes();
  return Repository.GripTypeDB.list();
};

export const getDigitData = async () => {
  await syncDigits();
  return Repository.DigitDB.list();
};

export const getLieDigitsData = async () => {
  await syncLieDigits();
  return Repository.LieDigitDB.list();
};

export const getLieFractionData = async () => {
  await syncLieFractions();
  return Repository.LieFractionDB.list();
};

export const getLoftData = async () => {
  await syncLofts();
  return Repository.LoftDB.list();
};

export const getLoftDigitData = async () => {
  await syncLoftDigits();
  return Repository.LoftDigitDB.list();
};

export const getLoftFractionData = async () => {
  await syncLoftFractions();
  return Repository.LoftFractionDB.list();
};

export const getPutterFaceData = async () => {
  await syncPutterFace();
  return Repository.PutterFaceDB.list();
};

export const getPutterGripData = async () => {
  await syncPutterGrip();
  return Repository.PutterGripDB.list();
};

export const getPutterHeadFinishData = async () => {
  await syncPutterHeadFinish();
  return Repository.PutterHeadFinishDB.list();
};

export const getPutterHeadModelData = async () => {
  await syncPutterHeadModel();
  return Repository.PutterHeadModelDB.list();
};

export const getPutterLevelData = async () => {
  await syncPutterLevel();
  return Repository.PutterLevelDB.list();
};

export const getPutterNeckData = async () => {
  await syncPutterNeck();
  return Repository.PutterNeckDB.list();
};

export const getPutterShaftData = async () => {
  await syncPutterShaft();
  return Repository.PutterShaftDB.list();
};

export const getPutterSightlineData = async () => {
  await syncPutterSightline();
  return Repository.PutterSightlineDB.list();
};

export const getPutterTypeData = async () => {
  await syncPutterType();
  return Repository.PutterTypeDB.list();
};

export const getPutterWeightData = async () => {
  await syncPutterWeight();
  return Repository.PutterWeightDB.list();
};

export const getHeadFinishWedgeData = async () => {
  await syncHeadFinishWedge();
  return Repository.HeadFinishWedgeDB.list();
};

export const getIronClubData = async () => {
  await syncIronClub();
  return Repository.IronClubDB.list();
};

export const getBsgBallData = async () => {
  await syncBsgBall();
  return Repository.BsgBallDB.list();
};

export const getBsgGloveData = async () => {
  await syncBsgGlove();
  return Repository.BsgGloveDB.list();
};

export const getBsgGloveSizeData = async () => {
  await syncBsgGloveSize();
  return Repository.BsgGloveSizeDB.list();
};

export const getBsgHeadwearData = async () => {
  await syncBsgHeadwear();
  return Repository.BsgHeadwearDB.list();
};

export const getBsgShoeData = async () => {
  await syncBsgShoe();
  return Repository.BsgShoeDB.list();
};

export const getBsgShoeSizeData = async () => {
  await syncBsgShoeSize();
  return Repository.BsgShoeSizeDB.list();
};

export const getBsgShoeWidthData = async () => {
  await syncBsgShoeWidth();
  return Repository.BsgShoeWidthDB.list();
};

export const getGolfBagData = async () => {
  await syncGolfBag();
  return Repository.GolfBagDB.list();
};

export const getTourData = async () => {
  await syncTours();
  return Repository.TourDB.list();
};

export const getLoftsAndHandAndHeadModelData = async () => {
  await syncLoftsAndHandAndHeadModel();
  return Repository.LoftsAndHandAndHeadModelDB.list();
};

export const getPlayerRegionLookups = async () => {
  await syncPlayerRegionLookups();
  return Repository.PlayerRegionLookupsDB.list();
};

export const getPlayerStatusLookups = async () => {
  await syncPlayerStatusLookups();
  return Repository.PlayerStatusLookupsDB.list();
};

export const getPlayerInternalStatuses = async () => {
  await syncPlayerInternalStatuses();
  return Repository.PlayerInternalStatusesDB.list();
};

export const getInternalStatusLookups = async () => {
  await syncInternalStatusLookups();
  return Repository.InternalStatusLookupsDB.list();
};

export const getNationalities = async () => {
  await syncNationalities();
  return Repository.NationalityDB.list();
};

export const getPlayerCollegeLookups = async () => {
  await syncPlayerCollegeLookups();
  return Repository.PlayerCollegeLookupDB.list();
};

export const getPlayerYearLookups = async () => {
  await syncPlayerYearLookups();
  return Repository.PlayerYearLookupDB.list();
};

export const getPlayerSupportLookups = async () => {
  await syncPlayerSupportLookups();
  return Repository.PlayerSupportLookupDB.list();
};

export const getFedExCupPlayers = async () => {
  await syncFedExCupPlayers();
  return Repository.FedExCupPlayerDB.list();
};
